/**
 * The subscribe ask — appears when the daily free preview runs out. The music keeps playing
 * (the visuals blur behind it); two Stripe payment links, and an honor-system "already
 * subscribed" continue until the entitlement Worker lands.
 */
import React, { useEffect, useState } from 'react'
import { PLANS, track, trackBeginCheckout } from './analytics'
import { isNativeAudio } from './audio/desktop'
import { checkoutUrl, isAppStore, MANAGE_SUBSCRIPTION_URL, offeredVolumes, openExternal, resolveFlavor, SUBSCRIBE } from './catalogue'
import { requestClaimLink } from './firebaseClaim'
import { sendSyqelPasswordReset, signInSyqelAccount } from './syqelAccount'
import { iapProducts, iapPurchase, iapRestore, openManageSubscriptions, type IapProduct } from './iap'
import { annualSavingsPct, PRICE_USD } from './pricing'

/** The paywall's volume line — the Pro volumes on offer right now ("Volume 2 & new volumes"). */
function premiumVolumesLabel(): string {
  const titles = offeredVolumes().filter((v) => v.pro).map((v) => v.title)
  return titles.length ? `${titles.join(', ')} & new volumes` : 'new volumes'
}

const linkBtn: React.CSSProperties = {
  display: 'block', width: '100%', boxSizing: 'border-box', padding: '13px 20px', fontSize: 15,
  fontFamily: 'monospace', textAlign: 'center', textDecoration: 'none', color: '#fff',
  background: 'rgba(124,91,255,0.4)', border: '1px solid rgba(124,91,255,0.8)', borderRadius: 10
}

export interface SubscribeModalProps {
  /** Why the modal opened: a 'support' click, or the PERIODIC ask (every few minutes of
   *  free playback — always refusable; the watermark is the price of refusing). */
  reason?: 'support' | 'periodic' | 'premium'
  /** "I already subscribed" — verify entitlement (signed cookie via /api/status) and close. */
  onSubscribed: () => void
  /** Dismiss without subscribing (support variant only). */
  onClose: () => void
  /** A short status line (e.g. "no active subscription found"). */
  notice?: string | null
}

export function SubscribeModal({ reason = 'support', onSubscribed, onClose, notice }: SubscribeModalProps): JSX.Element {
  // existing SYQEL (Desktop/VJ/...) subscribers claim access by email — any paid SYQEL
  // subscription includes SYQEL Art
  const [claiming, setClaiming] = useState(false)
  const [claimEmail, setClaimEmail] = useState('')
  const [claimSent, setClaimSent] = useState(false)
  const [claimPassword, setClaimPassword] = useState('')
  const [claimNote, setClaimNote] = useState<string | null>(null)
  // Mac App Store flavor: replace the Stripe links with StoreKit IAP.
  const [appStore, setAppStore] = useState(isAppStore())
  const [products, setProducts] = useState<IapProduct[]>([])
  const [busy, setBusy] = useState(false)
  const [iapNotice, setIapNotice] = useState<string | null>(null)
  useEffect(() => { track('subscribe_modal_view', { reason }) }, [reason])
  useEffect(() => {
    let alive = true
    void resolveFlavor().then((flavor) => {
      if (!alive) return
      const mas = flavor === 'appstore'
      setAppStore(mas)
      if (mas) void iapProducts().then((p) => { if (alive) setProducts(p) })
    })
    return () => { alive = false }
  }, [])
  const sendClaim = (): void => {
    const email = claimEmail.trim()
    if (!email.includes('@')) return
    void requestClaimLink(email, isNativeAudio()) // Firebase emails the sign-in link (same auth as Desktop/VJ)
    track('claim_link_request')
    setClaimSent(true)
  }
  // a plan bought on syqel.com lives on the SYQEL account: sign in to it (the password set in SYQEL
  // Live / Desktop / syqel.com); without a password, the emailed link claims through syqel.art's checkout
  const signIn = async (): Promise<void> => {
    const email = claimEmail.trim()
    if (!email.includes('@') || !claimPassword) return
    setBusy(true); setClaimNote(null)
    track('account_signin_attempt')
    const result = await signInSyqelAccount(email, claimPassword, isNativeAudio())
    setBusy(false)
    if (result === 'ok') { track('account_signin_paid'); onSubscribed(); return }
    setClaimNote(
      result === 'not-paid' ? 'signed in — this SYQEL account has no SYQEL Art plan'
        : result === 'wrong-credentials' ? 'wrong email or password — or use “email me a link” below'
          : 'couldn’t reach SYQEL — try again'
    )
  }
  const resetPassword = (): void => {
    const email = claimEmail.trim()
    if (!email.includes('@')) return
    void sendSyqelPasswordReset(email).then((ok) => setClaimNote(ok ? 'password reset email sent' : 'couldn’t send the reset email'))
  }
  const lineItem = (p: IapProduct): Record<string, unknown> => ({ item_id: p.id, item_name: p.displayName, price: p.amount, quantity: 1 })
  const buy = async (p: IapProduct): Promise<void> => {
    setBusy(true); setIapNotice(null)
    track('begin_checkout', { currency: p.currency, value: p.amount, items: [lineItem(p)] })
    try {
      const result = await iapPurchase(p.id)
      if (result === 'ok') {
        track('purchase', { transaction_id: `appstore:${p.id}:${Date.now()}`, currency: p.currency, value: p.amount, items: [lineItem(p)] })
        onSubscribed(); return
      }
      if (result === 'cancelled') setIapNotice(null)
      else if (result === 'pending') setIapNotice('purchase pending approval — you’ll be unlocked once it clears')
      else setIapNotice('purchase didn’t complete — please try again')
    } catch { setIapNotice('purchase failed') } finally { setBusy(false) }
  }
  const restore = async (): Promise<void> => {
    setBusy(true); setIapNotice(null)
    try {
      if (await iapRestore()) { track('iap_restore_success'); onSubscribed(); return }
      setIapNotice('no active subscription found to restore')
    } catch { setIapNotice('restore failed') } finally { setBusy(false) }
  }
  // annual first (mirrors the Stripe layout); App Store returns products price-ascending
  const orderedProducts = [...products].sort(
    (a, b) => (b.period.includes('year') ? 1 : 0) - (a.period.includes('year') ? 1 : 0)
  )
  // savings of annual vs 12× monthly — computed live from the store prices (no hardcoded %)
  const monthly = products.find((p) => p.period.includes('month'))
  const annual = products.find((p) => p.period.includes('year'))
  const savingsPct = monthly && annual ? annualSavingsPct(monthly.amount, annual.amount) : 0
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 30, display: 'grid', placeItems: 'center', background: 'rgba(4,4,10,0.55)', backdropFilter: 'blur(14px)' }} onClick={onClose}>
      <div style={{ width: 360, maxWidth: '88vw', padding: '26px 26px 18px', borderRadius: 14, background: 'rgba(10,10,18,0.92)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', textAlign: 'center', fontFamily: 'monospace' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontFamily: "'Changa', sans-serif", fontWeight: 700, fontSize: 26, letterSpacing: 3 }}>
          SYQEL<span style={{ fontSize: 10, verticalAlign: 'super' }}>®</span>
        </div>
        {/* what Pro buys, as two plain lines: the premium visuals first, then the watermark — which people
            kept missing at the tail of one long sentence (owner, 1.4.0) */}
        <div style={{ fontSize: 12.5, margin: '10px 0 18px', lineHeight: 1.55, textAlign: 'left', display: 'grid', gap: 6 }}>
          <div><span style={{ color: '#7c5bff', fontWeight: 700 }}>✓</span>&nbsp; <span style={{ opacity: 0.8 }}>Every premium visual — {premiumVolumesLabel()} as they arrive</span></div>
          <div><span style={{ color: '#7c5bff', fontWeight: 700 }}>✓</span>&nbsp; <span style={{ opacity: 0.8 }}>No SYQEL® watermark on your screen or your TV</span></div>
        </div>
        {appStore ? (
          orderedProducts.length === 0 ? (
            <div style={{ fontSize: 12, opacity: 0.6, padding: '12px 0' }}>loading plans…</div>
          ) : (
            orderedProducts.map((p, i) => (
              <button
                key={p.id}
                disabled={busy}
                onClick={() => void buy(p)}
                style={{ ...linkBtn, marginTop: i === 0 ? 0 : 10, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.55 : 1, ...(i === 0 ? {} : { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.25)' }) }}
              >
                {p.price}{p.period ? ` ${p.period}` : ''}
                {p.period.includes('year') && savingsPct > 0
                  ? <span style={{ opacity: 0.65, fontSize: 11 }}> · save {savingsPct}%</span>
                  : null}
              </button>
            ))
          )
        ) : (
          <>
            <a href={SUBSCRIBE.annual.url} target="_blank" rel="noreferrer" style={linkBtn} onClick={(e) => { e.preventDefault(); trackBeginCheckout(PLANS.annual); openExternal(checkoutUrl(SUBSCRIBE.annual.url)) }}>
              {SUBSCRIBE.annual.label} <span style={{ opacity: 0.65, fontSize: 11 }}>· save {annualSavingsPct(PRICE_USD.monthly, PRICE_USD.annual)}%</span>
            </a>
            <a href={SUBSCRIBE.monthly.url} target="_blank" rel="noreferrer" style={{ ...linkBtn, marginTop: 10, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.25)' }} onClick={(e) => { e.preventDefault(); trackBeginCheckout(PLANS.monthly); openExternal(checkoutUrl(SUBSCRIBE.monthly.url)) }}>
              {SUBSCRIBE.monthly.label}
            </a>
          </>
        )}
        {(notice || iapNotice) && <div style={{ marginTop: 12, fontSize: 11, color: '#ff9d9d' }}>{notice || iapNotice}</div>}
        {appStore ? (
          <button
            onClick={() => void restore()}
            disabled={busy}
            style={{ marginTop: 14, background: 'none', border: 'none', color: '#fff', opacity: busy ? 0.3 : 0.55, fontSize: 11.5, fontFamily: 'monospace', cursor: busy ? 'default' : 'pointer', textDecoration: 'underline' }}
          >
            Restore purchases
          </button>
        ) : (
          <button
            onClick={() => { track('subscribe_verify_click'); onSubscribed() }}
            style={{ marginTop: 14, background: 'none', border: 'none', color: '#fff', opacity: 0.55, fontSize: 11.5, fontFamily: 'monospace', cursor: 'pointer', textDecoration: 'underline' }}
          >
            I already subscribed — continue
          </button>
        )}
        {!appStore && <div style={{ marginTop: 8 }}>
          {!claiming ? (
            <button
              onClick={() => setClaiming(true)}
              style={{ background: 'none', border: 'none', color: '#9fd8c0', opacity: 0.75, fontSize: 11, fontFamily: 'monospace', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Bought on syqel.com, or a SYQEL subscriber? Sign in
            </button>
          ) : claimSent ? (
            <div style={{ fontSize: 11, opacity: 0.7 }}>check your inbox — open the SYQEL sign-in link on THIS device to unlock</div>
          ) : (
            <div style={{ display: 'grid', gap: 6, justifyItems: 'center' }}>
              <input
                type="email"
                placeholder="your SYQEL account email"
                value={claimEmail}
                onChange={(e) => setClaimEmail(e.target.value)}
                style={{ width: 230, background: '#15151f', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 6, padding: '6px 8px', font: '11px monospace' }}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  type="password"
                  placeholder="password"
                  value={claimPassword}
                  onChange={(e) => setClaimPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void signIn()}
                  style={{ width: 150, background: '#15151f', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 6, padding: '6px 8px', font: '11px monospace' }}
                />
                <button disabled={busy} onClick={() => void signIn()} style={{ background: 'rgba(124,91,255,0.4)', border: '1px solid rgba(124,91,255,0.8)', color: '#fff', borderRadius: 6, padding: '6px 10px', font: '11px monospace', cursor: 'pointer' }}>
                  sign in
                </button>
              </div>
              {claimNote && <div style={{ fontSize: 10.5, color: '#ffd28a' }}>{claimNote}</div>}
              <div style={{ fontSize: 10.5, opacity: 0.7 }}>
                no password?{' '}
                <a href="#" style={{ color: '#9fd8c0' }} onClick={(e) => { e.preventDefault(); sendClaim() }}>email me a link</a>
                {' · '}
                <a href="#" style={{ color: '#9fd8c0' }} onClick={(e) => { e.preventDefault(); resetPassword() }}>reset password</a>
              </div>
            </div>
          )}
        </div>}
        <button
          onClick={onClose}
          style={{ marginTop: 6, marginLeft: 14, background: 'none', border: 'none', color: '#fff', opacity: 0.45, fontSize: 11.5, fontFamily: 'monospace', cursor: 'pointer', textDecoration: reason === 'periodic' ? 'underline' : 'none' }}
        >
          {reason === 'periodic' ? 'no thanks — keep watching (watermark stays)' : 'maybe later'}
        </button>
        <div style={{ marginTop: 12, fontSize: 10.5, opacity: 0.4 }}>
          {appStore ? (
            <a href="#" style={{ color: '#fff' }} onClick={(e) => { e.preventDefault(); track('manage_subscription'); void openManageSubscriptions() }}>manage subscription</a>
          ) : (
            <a href={MANAGE_SUBSCRIPTION_URL} target="_blank" rel="noreferrer" style={{ color: '#fff' }} onClick={(e) => { e.preventDefault(); track('manage_subscription'); openExternal(MANAGE_SUBSCRIPTION_URL) }}>manage subscription</a>
          )}
          <span> · © SYQEL® INC</span>
        </div>
      </div>
    </div>
  )
}
